import { useId } from 'react';
import { useLocation } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { useApprovePasswordStore, useNotificationStore, useTransactionsStore } from '@lib/store';
import { cn, RoutePaths } from '@shared/utils';
import { Button, Constants, Input, Spinner } from '@shared/widgets';

interface IForm {
  password: string;
}
const DEFAULT_VALUES = { password: '' };

interface ApprovePasswordFormProps {
  className?: string;
  data?: { title: string; label?: string; value?: string | number }[];
  requestId?: string;
  onBack: () => void;
  onSubmit: () => void;
}

export const ApprovePasswordForm = (props: ApprovePasswordFormProps) => {
  const location = useLocation();

  const approvePasswordStore = useApprovePasswordStore();
  const transactionsStore = useTransactionsStore();
  const notificationStore = useNotificationStore();

  const id = useId();
  const { control, formState, handleSubmit, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });

  const backHandler = () => {
    props.onBack?.();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: IForm) => {
    const result = await approvePasswordStore.approvePassword({ RequestId: props.requestId, Password: data.password });
    if (result.data) {
      notificationStore.setSuccess(Constants.DEFAULT_SUCCESS_MESSAGE);
      if (location.pathname === RoutePaths.payments.onNavigate()) transactionsStore.getTransactions('c10b71ee-0f03-e711-80c4-00155d5bc507');
      props.onSubmit?.();
    }
  };

  return (
    <div className={cn('flex w-full flex-col gap-4', props.className)}>
      <Spinner show={formState.isSubmitting} fullscreen />
      <div>{props.data?.map((item, idx) => <Item key={idx} {...item} />)}</div>
      <form className='flex flex-col gap-4' noValidate onSubmit={handleSubmit(submitHandler)}>
        <Controller
          name='password'
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className='flex flex-col gap-1'>
              <label className='text-sm font-semibold text-[#666] uppercase' htmlFor={id}>
                To confirm Top Up please enter your password
              </label>
              <Input
                {...field}
                className='h-[48px]'
                variant='outlined'
                rounded
                id={id}
                name='Password'
                placeholder='Password'
                type='password'
                required
              />
            </div>
          )}
        />
        <div className='flex gap-4'>
          <Button color='outlined' fullWidth onClick={backHandler}>
            Back
          </Button>
          <Button color='primary' fullWidth type='submit' disabled={!formState.isValid}>
            Confirm
          </Button>
        </div>
      </form>
    </div>
  );
};

interface ItemProps {
  className?: string;
  title: string;
  label?: string;
  value?: string | number;
}

const Item = (props: ItemProps) => {
  return (
    <div className={cn('flex flex-col gap-1', props.className)}>
      <h3 className='text-xs font-semibold text-[#929292] uppercase'>{props.title}</h3>
      <div className='flex gap-5'>
        <h4 className='text-sm font-semibold text-[#fcfcfc] uppercase'>{props.label}</h4>
        {props.value && <p className='text-sm font-semibold whitespace-nowrap text-[#fcfcfc] uppercase'>{props.value}</p>}
      </div>
    </div>
  );
};
